/**
 * ActiveRingersBar — the incoming-call drop-down banner (owner 2026-10-10, reworked
 * 2026-10-12).
 *
 * Owner directives (2026-10-12):
 *  - the bar shows up ONLY while a ringer is genuinely RINGING (unanswered). An
 *    answered ring is a call in progress — it must not keep the bar up, and after
 *    the END button retires the ring the bar is simply gone;
 *  - it must DROP DOWN properly at the top — fully visible below the iPhone
 *    notch (safe-area padding), taller than the old thin strip that "only peeped
 *    out a little bit";
 *  - it can be PUSHED AWAY (✕ snoozes every ringing line — they come back on
 *    backoff) or answered/declined inline. The chevron still expands the detail
 *    panel (all tracked ringers: ringing / in-call / snoozed).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PhoneCall, PhoneOff, BellRing, ChevronDown, ChevronUp, Clock, Volume2, X } from 'lucide-react';
import { useActiveRings } from '../hooks/useActiveRings';
import { resolveNotificationUrl } from './NotificationBell';
import type { ActiveRing } from '../api/rings';

function RingerPulse() {
  return (
    <span className="relative flex h-2.5 w-2.5 flex-shrink-0" aria-hidden="true">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/70" />
      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
    </span>
  );
}

export default function ActiveRingersBar() {
  const navigate = useNavigate();
  const { rings, activeCount, answer, snooze } = useActiveRings(true);
  const [open, setOpen] = useState(false);

  // Only UNANSWERED ringers keep the banner up (owner 2026-10-12). Answered rings
  // are calls that already happened; the END button dismisses them server-side.
  const ringing = rings.filter((r) => r.ring_status === 'ringing');
  const others = rings.filter((r) => r.ring_status !== 'ringing');

  // Keep the panel closed if the last ringer goes away.
  useEffect(() => {
    if (ringing.length === 0 && others.length === 0) setOpen(false);
  }, [ringing.length, others.length]);

  // Voice-call consolidation (owner report 2026-10-13): the bar exists ONLY while
  // a line is genuinely RINGING. An answered ring is a call in progress — the bar
  // must not keep 'calling' at the top of the screen after the user answers.
  if (ringing.length === 0) return null;

  const names = ringing.map((r) => r.agent).join(', ');
  const isSingle = ringing.length === 1;

  const answerRing = (r: ActiveRing) => {
    void answer(r.id);
    // Unlock audio inside the gesture (iOS only honours an AudioContext unlock
    // while the tap is still "active").
    try {
      const Ctx = (window as unknown as { AudioContext?: typeof AudioContext }).AudioContext;
      if (Ctx) { const c = new Ctx(); void c.resume().then(() => c.close().catch(() => {})).catch(() => {}); }
    } catch { /* ignore */ }
    // CONSOLIDATION (owner report 2026-10-13): the bar cannot speak, so it hands
    // the answer to the widget — the ONE call experience — which speaks the
    // agent's reason. No second, silent call path.
    try {
      window.dispatchEvent(new CustomEvent('saik:ring-answer', {
        detail: { id: r.id, name: r.agent, body: r.question || r.body || r.title || '', url: r.url },
      }));
    } catch { /* ignore */ }
    navigate(resolveNotificationUrl(r.url || '/app/chat'));
  };

  // Push it away: snooze EVERY ringing line (owner 2026-10-12 — "it should be
  // able to push and go away"). They come back automatically on backoff; the
  // agent keeps watching.
  const pushAway = () => {
    ringing.forEach((r) => void snooze(r.id));
    setOpen(false);
  };

  return (
    <div
      className="relative z-[55] flex-none"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      data-agent-id="ringers.bar"
      data-ringing-count={ringing.length}
    >
      {/* The drop-down banner — taller, fully below the notch, actionable inline */}
      <div className="flex w-full items-center gap-2 bg-emerald-600 px-4 py-2.5 text-white shadow-lg shadow-emerald-900/20">
        <span className="relative flex h-4 w-4 flex-shrink-0 items-center justify-center">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/60" />
          <BellRing size={16} className="relative" />
        </span>
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          aria-expanded={open}
          aria-label={`${ringing.length} active ringer${isSingle ? '' : 's'} — view`}
        >
          <span className="truncate text-sm font-semibold tracking-wide">
            {isSingle ? `${names} is calling…` : `${ringing.length} agents calling…`}
          </span>
          {!isSingle && (
            <span className="hidden truncate text-xs text-emerald-50/90 sm:inline">{names}</span>
          )}
          <span className="ml-auto flex flex-shrink-0 items-center gap-1 text-[11px] font-medium">
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {open ? 'Hide' : 'View'}
          </span>
        </button>

        {/* Inline actions — answer / decline without expanding */}
        {isSingle ? (
          <button
            onClick={() => answerRing(ringing[0])}
            data-agent-id="ringers.inline-answer"
            className="flex flex-shrink-0 items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-emerald-700 hover:bg-emerald-50"
          >
            <PhoneCall size={13} /> Answer
          </button>
        ) : null}
        <button
          onClick={pushAway}
          data-agent-id="ringers.push-away"
          title="Push away — snooze all ringing lines (they come back)"
          aria-label="Push away — snooze all ringing lines"
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-emerald-50 hover:bg-emerald-500/60"
        >
          <X size={15} />
        </button>
      </div>

      {/* The expandable panel — every tracked ringer with its live ring + actions */}
      {open && (
        <div className="absolute left-0 right-0 top-full z-[55] max-h-[60vh] overflow-y-auto border-b border-emerald-700/30 bg-white shadow-xl dark:bg-slate-900">
          <div className="mx-auto max-w-3xl space-y-2 p-3">
            {[...ringing, ...others].map((r) => {
              const inCall = r.ring_status === 'answered' || r.ring_status === 'active';
              const sleeping = r.ring_status === 'snoozed';
              return (
                <div
                  key={r.id}
                  data-agent-id={`ringers.item.${r.id}`}
                  className={`rounded-xl border p-3 ${
                    sleeping
                      ? 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/40'
                      : inCall
                      ? 'border-indigo-200 bg-indigo-50 dark:border-indigo-800 dark:bg-indigo-900/20'
                      : 'border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/20'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      {sleeping ? (
                        <Clock size={16} className="flex-shrink-0 text-slate-400" />
                      ) : inCall ? (
                        <PhoneCall size={16} className="flex-shrink-0 text-indigo-500" />
                      ) : (
                        <span className="relative flex h-3 w-3 flex-shrink-0">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" />
                          <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
                        </span>
                      )}
                      <span className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {r.agent}
                      </span>
                      {r.severity && (
                        <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${
                          r.severity === 'urgent' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                          : r.severity === 'high' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                          : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                        }`}>
                          {r.severity}
                        </span>
                      )}
                      <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${
                        sleeping ? 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                        : inCall ? 'bg-indigo-600 text-white'
                        : 'bg-emerald-600 text-white'
                      }`}>
                        {sleeping ? 'snoozed' : inCall ? 'in call' : 'ringing'}
                      </span>
                    </div>
                    <span className="flex-shrink-0 text-[10px] text-slate-400">
                      {r.ring_count} ring{r.ring_count === 1 ? '' : 's'}
                    </span>
                  </div>

                  {r.question && (
                    <p className="mt-1.5 line-clamp-2 text-xs text-slate-600 dark:text-slate-300">{r.question}</p>
                  )}
                  {sleeping && r.snooze_until && (
                    <p className="mt-1 text-[10px] text-slate-400">
                      Snoozed — the agent will ring again automatically.
                    </p>
                  )}

                  <div className="mt-2 flex flex-wrap gap-2">
                    {!sleeping && !inCall && (
                      <button
                        onClick={() => answerRing(r)}
                        className="flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500"
                      >
                        <PhoneCall size={13} /> Answer
                      </button>
                    )}
                    <button
                      onClick={() => void snooze(r.id)}
                      disabled={sleeping || inCall}
                      title={sleeping ? 'Already snoozed' : 'Decline (snooze) — the agent keeps watching'}
                      className="flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-40 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
                    >
                      <Volume2 size={13} /> Snooze
                    </button>
                    <button
                      onClick={() => void import('../api/rings').then((m) => m.ringsApi.dismiss(r.id))}
                      title="Stop this line (dismiss permanently)"
                      className="flex items-center gap-1.5 rounded-full border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/20"
                    >
                      <PhoneOff size={13} /> Dismiss
                    </button>
                  </div>
                </div>
              );
            })}
            <p className="px-1 pt-1 text-[10px] text-slate-400">
              Answering starts the conversation; declining snoozes (the agent keeps watching). Ending a
              call stops all ringing for that session.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
